'use client';
/**
 * ClubMap — componente Mapbox GL JS lazy-loaded para /clubes/mapa (D9 do ADR).
 *
 * - Importa mapbox-gl dinamicamente no useEffect (requestIdleCallback)
 *   para não bloquear LCP de outras rotas
 * - Mostra fallback gracioso quando token ausente ou load falha
 * - Token recebido como prop (nunca lê process.env aqui)
 * - Markers diferenciam partner vs listed visualmente
 * - Bottom-sheet (mobile) / popover (desktop) via state local
 */
import * as React from 'react';

export interface ClubMapItem {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  integration_status: 'listed' | 'partner';
  neighborhood: string | null;
  location_lat: number;
  location_lng: number;
}

export interface ClubMapProps {
  clubs: ClubMapItem[];
  locale: 'pt' | 'es';
  /** NEXT_PUBLIC_MAPBOX_TOKEN — recebido do Server Component */
  mapboxToken: string | undefined;
  /** Callback de tracking ao clicar em marker */
  onMarkerClick?: (clubSlug: string, integrationStatus: 'listed' | 'partner') => void;
  /** Callback disparado quando mapa finaliza carregamento (para Map Loaded event) */
  onMapLoaded?: (markersCount: number, tokenActive: boolean) => void;
}

export function ClubMap({
  clubs,
  locale,
  mapboxToken,
  onMarkerClick,
  onMapLoaded,
}: ClubMapProps): React.JSX.Element {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const mapRef = React.useRef<unknown>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedClub, setSelectedClub] = React.useState<ClubMapItem | null>(null);
  const [isLoaded, setIsLoaded] = React.useState(false);

  const copy = {
    mapUnavailable:
      locale === 'pt'
        ? 'Mapa indisponível no momento.'
        : 'Mapa no disponible en este momento.',
    seeList: locale === 'pt' ? 'Ver lista de clubs' : 'Ver lista de clubs',
    partner: locale === 'pt' ? 'Parceiro Brisa' : 'Socio Brisa',
    listed: locale === 'pt' ? 'Catalogado' : 'Catalogado',
    viewProfile: locale === 'pt' ? 'Ver perfil' : 'Ver perfil',
    close: locale === 'pt' ? 'Fechar' : 'Cerrar',
  };

  // Lazy-load mapbox-gl APENAS quando este componente monta
  React.useEffect(() => {
    if (!mapboxToken) {
      setError('no_token');
      onMapLoaded?.(0, false);
      return;
    }

    let cancelled = false;

    const schedule = (cb: () => void): void => {
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(cb, { timeout: 2000 });
      } else {
        window.setTimeout(cb, 1);
      }
    };

    schedule(async () => {
      try {
        const mod = await import('mapbox-gl');
        // @ts-expect-error: mapbox-gl CSS import
        await import('mapbox-gl/dist/mapbox-gl.css');
        if (!cancelled && containerRef.current && !mapRef.current) {
          const mapboxgl = mod.default;
          mapboxgl.accessToken = mapboxToken;

          const map = new mapboxgl.Map({
            container: containerRef.current,
            style: 'mapbox://styles/mapbox/light-v10',
            // TODO: [DESIGN AGENT] — trocar por style customizado Brisa (Verde Profundo dominante, markers em Lilás Bruma)
            center: [2.1734, 41.3851], // Barcelona
            zoom: 13,
          });

          map.on('load', () => {
            if (cancelled) return;

            // Adicionar markers para cada club
            clubs.forEach((club) => {
              const el = document.createElement('div');
              el.className = `brisa-map-marker brisa-map-marker--${club.integration_status}`;
              el.setAttribute('aria-label', `${club.name} — ${club.integration_status === 'partner' ? copy.partner : copy.listed}`);
              el.setAttribute('role', 'button');
              el.setAttribute('tabindex', '0');

              // Estilos inline do marker (fallback se CSS não carregou)
              el.style.width = '32px';
              el.style.height = '32px';
              el.style.borderRadius = '50%';
              el.style.backgroundColor =
                club.integration_status === 'partner' ? '#DED0EA' : '#557A5F';
              el.style.border = '2px solid #234536';
              el.style.cursor = 'pointer';
              el.style.display = 'flex';
              el.style.alignItems = 'center';
              el.style.justifyContent = 'center';
              el.style.fontSize = '14px';

              const marker = new mapboxgl.Marker({ element: el })
                .setLngLat([club.location_lng, club.location_lat])
                .addTo(map);

              const handleClick = () => {
                setSelectedClub(club);
                onMarkerClick?.(club.slug, club.integration_status);
              };

              el.addEventListener('click', handleClick);
              el.addEventListener('keydown', (e: KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') handleClick();
              });

              // Cleanup no unmount do marker é feito pelo map.remove()
              void marker;
            });

            setIsLoaded(true);
            onMapLoaded?.(clubs.length, true);
          });

          mapRef.current = map;
        }
      } catch {
        if (!cancelled) {
          setError('map_load_failed');
          onMapLoaded?.(0, false);
        }
      }
    });

    return () => {
      cancelled = true;
      if (mapRef.current) {
        (mapRef.current as { remove: () => void }).remove();
        mapRef.current = null;
      }
    };
  }, [mapboxToken]);

  if (error) {
    return (
      <div
        role="alert"
        className="brisa-club-map-fallback"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px',
          padding: '48px 24px',
          backgroundColor: 'var(--brisa-surface, #234536)',
          color: 'var(--brisa-text, #DED0EA)',
          borderRadius: '12px',
          textAlign: 'center',
        }}
      >
        <p style={{ margin: 0, fontSize: '1rem' }}>{copy.mapUnavailable}</p>
        <a
          href={`/clubes`}
          style={{
            color: 'var(--brisa-accent, #DED0EA)',
            textDecoration: 'underline',
            fontSize: '0.875rem',
          }}
        >
          {copy.seeList}
        </a>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      {/* Mapa */}
      <div
        ref={containerRef}
        role="region"
        aria-label={locale === 'pt' ? 'Mapa de clubs em Barcelona' : 'Mapa de clubs en Barcelona'}
        style={{ width: '100%', height: '70vh', minHeight: '400px' }}
      />

      {/* Skeleton loading */}
      {!isLoaded && !error && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'var(--brisa-surface-muted, #365A47)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--brisa-text-dim, #557A5F)',
            fontSize: '0.875rem',
          }}
        >
          {locale === 'pt' ? 'Carregando mapa…' : 'Cargando mapa…'}
        </div>
      )}

      {/* Mini-card / bottom-sheet ao selecionar marker */}
      {selectedClub && (
        <div
          role="dialog"
          aria-modal="false"
          aria-label={selectedClub.name}
          style={{
            position: 'absolute',
            bottom: '16px',
            left: '16px',
            right: '16px',
            maxWidth: '400px',
            margin: '0 auto',
            backgroundColor: 'var(--brisa-surface, #234536)',
            color: 'var(--brisa-text, #DED0EA)',
            borderRadius: '12px',
            padding: '16px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <p style={{ margin: 0, fontWeight: 600 }}>{selectedClub.name}</p>
              {selectedClub.neighborhood && (
                <p style={{ margin: 0, fontSize: '0.75rem', opacity: 0.7 }}>{selectedClub.neighborhood}</p>
              )}
              <p
                style={{
                  margin: '4px 0 0',
                  fontSize: '0.75rem',
                  backgroundColor:
                    selectedClub.integration_status === 'partner'
                      ? 'rgba(222,208,234,0.15)'
                      : 'rgba(85,122,95,0.2)',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  display: 'inline-block',
                }}
              >
                {selectedClub.integration_status === 'partner' ? copy.partner : copy.listed}
              </p>
            </div>
            <button
              onClick={() => setSelectedClub(null)}
              aria-label={copy.close}
              style={{
                background: 'none',
                border: 'none',
                color: 'inherit',
                cursor: 'pointer',
                fontSize: '1.25rem',
                lineHeight: 1,
                padding: '4px',
              }}
            >
              ×
            </button>
          </div>
          <a
            href={`/clubes/${selectedClub.slug}`}
            style={{
              display: 'inline-block',
              color: 'var(--brisa-accent, #DED0EA)',
              textDecoration: 'underline',
              fontSize: '0.875rem',
            }}
          >
            {copy.viewProfile} →
          </a>
        </div>
      )}

      {/* Link acessível "Ver lista" sempre visível (AC-24.8 + a11y) */}
      <a
        href="/clubes"
        style={{
          display: 'block',
          textAlign: 'center',
          padding: '12px',
          color: 'var(--brisa-text-dim, #557A5F)',
          fontSize: '0.875rem',
          textDecoration: 'underline',
        }}
        aria-label={locale === 'pt' ? 'Ver lista completa de clubs' : 'Ver lista completa de clubs'}
      >
        {copy.seeList}
      </a>
    </div>
  );
}
